/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, memo, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Typography } from 'antd';
import NodeWrapper from '../../NodeWrapper';
import { GraphContext } from '../../FlowGraph';
import { GRAPH_LAYOUTS } from '../../../../constants/flowGraph';
import LimitedConnectionsHandle from '../../LimitedConnectionsHandle';

// Misc imports
import { NODE_TYPES_PROPERTIES } from '../../../../constants/flowGraph';
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';

// Constant declarations
const { Text } = Typography;

const SwitchVariableNode = ({ id, type, data, dragging, selected }) => {
  const nodeProps = NODE_TYPES_PROPERTIES[type];
  const { t } = useTranslation();
  const { graphDirection } = useContext(GraphContext);
  const { currentBotLanguage } = useBotLanguage();

  const NodeContents = useMemo(() => {
    return (
      <Fragment>
        <LimitedConnectionsHandle type="target" position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'top' : 'left'}/>

        <div className="content">
          {nodeProps.icon}

          <Text>
            {t('Flow control')}
          </Text>

          {
            data.switchVariable ? (
              <Text className="text-color-default" ellipsis>
                {data.switchVariable}
              </Text>
            ) : (
              <Text className="text-secondary">
                {t('Click to edit content')}
              </Text>
            )
          }
        </div>

        <LimitedConnectionsHandle type="source" position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'bottom' : 'right'}/>

        <div className="bg-shape-diamond"></div>
      </Fragment>
    );
  }, [data, nodeProps, graphDirection, currentBotLanguage]);

  return (
    <NodeWrapper
      id={id}
      type={type}
      data={data}
      children={NodeContents}
      dragging={dragging}
      selected={selected}
    />
  );
};

// https://reactflow.dev/docs/api/nodes/node-options/
SwitchVariableNode.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string,
  dragging: PropTypes.bool,
  selected: PropTypes.bool,
  type: PropTypes.string,
  xPos: PropTypes.number,
  yPos: PropTypes.number
};

export default memo(SwitchVariableNode);
